import React from 'react';
import { Check } from 'lucide-react';

interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  disabled?: boolean;
  className?: string;
}

export const Checkbox: React.FC<CheckboxProps> = ({
  checked,
  onChange,
  label,
  disabled = false,
  className = '',
}) => {
  return (
    <label
      className={`inline-flex items-center gap-2 cursor-pointer select-none ${
        disabled ? 'opacity-50 cursor-not-allowed' : ''
      } ${className}`}
    >
      <span className="relative inline-flex">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => !disabled && onChange(e.target.checked)}
          className="peer sr-only"
          disabled={disabled}
        />
        <span
          className={`
            w-[18px] h-[18px] rounded border transition-colors duration-150
            flex items-center justify-center
            peer-focus-visible:ring-2 peer-focus-visible:ring-accent peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-canvas
            ${checked
              ? 'bg-accent border-accent'
              : 'bg-surface border-line-strong hover:border-fg'
            }
          `}
        >
          {checked && <Check size={13} className="text-accent-fg" strokeWidth={3} />}
        </span>
      </span>
      {label && <span className="text-sm text-fg">{label}</span>}
    </label>
  );
};

interface CheckboxGroupProps {
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (selected: string[]) => void;
  className?: string;
}

export const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  options,
  selected,
  onChange,
  className = '',
}) => {
  const handleToggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {options.map((option) => (
        <Checkbox
          key={option.value}
          checked={selected.includes(option.value)}
          onChange={() => handleToggle(option.value)}
          label={option.label}
        />
      ))}
    </div>
  );
};
